import React, { useEffect, useMemo } from 'react'
import { flexRender, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import { createColumnsWithCheckbox, getColumsVisibility, getCorrectPageCount } from '../../../utils/table.utils';
import { includes, isArray } from 'lodash';
import { searchUtils } from '../../../utils/search.utils';
import LoadingSpinner from '../Loading/LoadingSpinner';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';



const DaisyTable = ({ data, setData, columnsDefs, fetchPage, goToContentOnRowClick, serverPages, sorting, setSorting, pagination, setPagination, pageCount, setPageCount, globalFilter, selection, setSelection, setTableContext, columnsVisibility, setColumnsVisibility, searchType, isLoading, withCheckbox, width }) => {

    const columns = useMemo(() => {
        return withCheckbox ? [createColumnsWithCheckbox(), ...columnsDefs] : [...columnsDefs];
    }, [columnsDefs, withCheckbox])


    const tableData = useMemo(() => {
        if (!data) return []
        let returnedData = [...data.values]
        if (globalFilter.value.length > 0 && globalFilter.keys.length > 0) {
            const searchFn = searchType === searchUtils.SEARCH_TYPES.fuzzy ? searchUtils.fuzzySearch : searchUtils.exactSearch
            returnedData = searchFn(returnedData, globalFilter.value, globalFilter.keys)
        }
        if (sorting.length > 0) {
            const sort = sorting[0]
            const { id, desc } = sort
            const sortFn = columnsDefs.find(column => column.id === id)?.sortingFn
            if (sortFn && typeof sortFn === 'function') {
                returnedData.sort((a, b) => {
                    return sortFn(a, b, id, desc)
                })
            }
        }

        const startRow = pagination.pageIndex * pagination.pageSize
        const endRow = startRow + pagination.pageSize
        return returnedData.slice(startRow, endRow);
    }, [data, pagination, globalFilter, sorting, searchType])

    const handlePaginationChange = (updater) => {
        if (typeof updater !== "function") return;
        const state = updater(table.getState().pagination);
        const formattedClientIndexToServer = parseInt(((state.pageIndex) * state.pageSize) / data.pagination.max_per_query) + 1

        if (isArray(serverPages) && !includes(serverPages, formattedClientIndexToServer) && formattedClientIndexToServer > 0) {
            fetchPage(formattedClientIndexToServer)
        }
        const correctPageCount = getCorrectPageCount(data.pagination.total, state.pageSize)
        setPagination(state)
        setPageCount(correctPageCount > 0 ? correctPageCount : 1)
    }


    const table = useReactTable(
        {
            data: tableData,
            columns: columns,
            columnResizeMode: 'onChange',
            columnResizeDirection: 'ltr',
            defaultColumn: {
                size: 150, //starting column size
                minSize: 20, //enforced during column resizing
                maxSize: Number.MAX_SAFE_INTEGER, //enforced during column resizing
            },
            state: {
                sorting,
                pagination,
                rowSelection: selection,
                columnVisibility: columnsVisibility
            },
            onPaginationChange: handlePaginationChange,
            onSortingChange: setSorting,
            onColumnVisibilityChange: setColumnsVisibility,
            onRowSelectionChange: setSelection,
            getCoreRowModel: getCoreRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            manualPagination: true, //turn off client-side pagination,
            manualSorting: true, //turn off client-side sorting
            manualFiltering: true, //turn off client-side filtering
            enableRowSelection: true,
            pageCount: pageCount,
            rowCount: data?.pagination?.total || 0,
            getRowId: row => row.id,
            meta: {
                getRowsByIds: (ids) => {
                    const formattedIds = Object.keys(ids).map(id => parseInt(id))
                    const rows = data.values.filter(row => formattedIds.includes(row.id))
                    console.log(rows)
                    console.log(formattedIds)
                    return rows
                },
                updateColumn: (rowIndex, columnId, value) => {
                    // Skip page index reset until after next rerender
                    setData(old =>
                        old.map((row, index) => {
                            if (index === rowIndex) {
                                return {
                                    ...old[rowIndex],
                                    [columnId]: value,
                                }
                            }
                            return row
                        })
                    )
                },
                updateFullRow: (rowIndex, updatedRow) => {
                    const updatedData = [...data.values]
                    updatedData[rowIndex] = updatedRow
                    setData({
                        ...data,
                        values: updatedData
                    })
                },
                updateCustomerRow: (customerId, updatedRow) => {
                    const updatedData = [...data.values]
                    const index = updatedData.findIndex(row => row.id === customerId)
                    if (index > -1) {
                        updatedData[index] = updatedRow
                    }
                    setData({
                        ...data,
                        values: updatedData
                    })
                },
                removeRow: (rowIndex) => {
                    const updatedData = [...data.values]
                    const removedRow = updatedData.splice(rowIndex, 1)
                    const index = data.values.indexOf(removedRow)
                    if (index > -1) {
                        updatedData.splice(index, 1)
                    }
                    setData({
                        ...data,
                        values: updatedData
                    })
                },
                removeBatchRows: (rowIndexes, byId = false) => {
                    const dataCopy = [...data.values]
                    let rowToRemove = []
                    if (byId) {
                        rowToRemove = rowIndexes.map(id => data.values.find(row => row.id === id))
                    } else {
                        rowToRemove = rowIndexes.map(index => data.values[index])
                    }
                    rowToRemove.forEach(row => {
                        const index = dataCopy.indexOf(row)
                        dataCopy.splice(index, 1)
                    })

                    setData({
                        ...data,
                        values: dataCopy
                    })
                }
            }


        });

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel().rows;




    useEffect(() => {
        setTableContext(table)
        setColumnsVisibility(getColumsVisibility(columns))
    }, [])

    useEffect(() => {
        if (globalFilter.value.length === 0)
            setPagination(prevPagination => ({
                ...prevPagination,
                pageIndex: 0
            }));
    }, [globalFilter.value]);

    if (isLoading) return <LoadingSpinner />
    return (
        <div className="overflow-hidden">



            <table style={{ width: width ? width : table.getCenterTotalSize() }} className="table  table-sm overflow-x-auto border-none m-auto border-spacing-0">
                <thead>
                    <tr className='text-sm'>
                        {headers.map((header) => {
                            const direction = header.column.getIsSorted();
                            const arrow = {
                                asc: <ChevronUpIcon className="w-4 h-4" />,
                                desc: <ChevronDownIcon className="w-4 h-4" />,
                            };
                            const sort_indicator = direction && arrow[direction];

                            return (
                                <th key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }} className='relative'>
                                    {header.isPlaceholder ? null : (
                                        <div
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="cursor-pointer flex gap-4"
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {direction && <span>{sort_indicator}</span>}


                                        </div>
                                    )}

                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr onClick={goToContentOnRowClick ? goToContentOnRowClick(row) : undefined} key={row.id} className={`hover hover:bg-base-300 transition-all duration-300 ${goToContentOnRowClick ? 'cursor-pointer' : ''}`}>
                            {row.getVisibleCells().map((cell, index) => {
                                if (cell.column.id === 'select' && index === 0) return (
                                    <td onClick={(e) => e.stopPropagation()} key={cell.id} style={{ width: cell.column.getSize() }} >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                                return (
                                    <td key={cell.id} style={{ width: cell.column.getSize() }} >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>

            </table>
        </div>
    )
}

export default DaisyTable
